const CANONICAL_HOSTS = {
  github: "github.com",
  gitlab: "gitlab.com",
};
function normalizePath(value) {
  return (value ?? "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^\/+|\/+$/g, "");
}
export function instanceHostFromUrl(value) {
  const normalized = normalizePath(value);
  if (!normalized) return null;
  return normalized.split("/")[0]?.toLowerCase() ?? null;
}
function explicitProvider(value) {
  const normalized = value?.trim().toLowerCase();
  return normalized === "github" || normalized === "gitlab" || normalized === "fixtures"
    ? normalized
    : null;
}
export function providerFromStorageKey(value) {
  const first = normalizePath(value).split("/")[0]?.toLowerCase();
  if (first === "github.com") return "github";
  if (first === "gitlab.com") return "gitlab";
  if (first === "fixtures") return "fixtures";
  return null;
}
/**
 * Resolve a project value at an API/UI boundary.
 *
 * A segment is removed as a host only when it matches the connection host (or
 * a canonical provider host). In particular, dots in a namespace never imply
 * that the namespace is a host. Durable identity is always instance-qualified;
 * callers that need compatibility with a legacy raw alias must make that read
 * fallback explicit rather than allowing the alias to remain a writer.
 */
export function projectIdentityAtIngress(value, hint = {}) {
  const storageKey = normalizePath(value);
  if (!storageKey) return null;
  const segments = storageKey.split("/").filter(Boolean);
  const fixture = segments[0]?.toLowerCase() === "fixtures";
  if (fixture) {
    const githubFlavor = segments[1]?.toLowerCase() === "github";
    const namespacePath = segments.slice(githubFlavor ? 2 : 1).join("/");
    if (!namespacePath) return null;
    const canonicalStorageKey = storageKey;
    return {
      provider: "fixtures",
      instanceHost: "fixtures",
      namespacePath,
      canonicalStorageKey,
      storageKey: canonicalStorageKey,
      fixtureFlavor: githubFlavor ? "github" : "gitlab",
    };
  }
  const provider = explicitProvider(hint.provider) ?? providerFromStorageKey(storageKey);
  if (provider !== "github" && provider !== "gitlab") return null;
  const instanceHost = instanceHostFromUrl(hint.instanceUrl) ?? CANONICAL_HOSTS[provider];
  const first = segments[0]?.toLowerCase();
  // Canonical provider prefixes are recognizable schema markers even when a
  // stale UI value is being moved between providers. Custom hosts are stripped
  // only when they match the active connection.
  const knownHosts = new Set([instanceHost, ...Object.values(CANONICAL_HOSTS)]);
  const namespacePath = segments.slice(knownHosts.has(first) ? 1 : 0).join("/");
  if (!namespacePath) return null;
  const canonicalStorageKey = `${instanceHost}/${namespacePath}`;
  return {
    provider,
    instanceHost,
    namespacePath,
    canonicalStorageKey,
    storageKey: canonicalStorageKey,
  };
}
/**
 * Parse a value whose contract already says it is a provider-scoped storage
 * key. This is intentionally separate from ingress parsing: the first segment
 * is structural here, not guessed from its spelling.
 */
export function projectIdentityFromStorageKey(value, hint = {}) {
  const storageKey = normalizePath(value);
  if (!storageKey) return null;
  if (storageKey.toLowerCase().startsWith("fixtures/")) {
    return projectIdentityAtIngress(storageKey, { provider: "fixtures" });
  }
  const segments = storageKey.split("/").filter(Boolean);
  const hintedProvider = explicitProvider(hint.provider);
  const detectedProvider = providerFromStorageKey(storageKey);
  const provider = detectedProvider ?? hintedProvider;
  if (provider !== "github" && provider !== "gitlab") return null;
  const hintedHost = instanceHostFromUrl(hint.instanceUrl);
  const instanceHost = (
    hintedHost ??
    (detectedProvider ? segments[0] : null) ??
    CANONICAL_HOSTS[provider]
  ).toLowerCase();
  const hostIsPresent = segments[0]?.toLowerCase() === instanceHost;
  const namespacePath = segments.slice(hostIsPresent ? 1 : 0).join("/");
  if (!namespacePath) return null;
  const canonicalStorageKey = `${instanceHost}/${namespacePath}`;
  return {
    provider,
    instanceHost,
    namespacePath,
    canonicalStorageKey,
    storageKey: canonicalStorageKey,
  };
}
/** Canonical-first, bounded compatibility candidates for legacy alias reads. */
export function projectStorageReadCandidates(identity, ingressValue) {
  const legacy = normalizePath(ingressValue);
  return Array.from(
    new Set([identity.canonicalStorageKey, ...(legacy ? [legacy] : [])].filter(Boolean))
  );
}
export function qualifyProjectStorageKey(provider, namespacePath, instanceUrl) {
  const normalizedPath = normalizePath(namespacePath);
  const providerId = explicitProvider(provider);
  if (!normalizedPath || !providerId) return normalizedPath;
  if (providerId === "fixtures") {
    return normalizedPath.startsWith("fixtures/") ? normalizedPath : `fixtures/${normalizedPath}`;
  }
  const identity = projectIdentityAtIngress(normalizedPath, { provider: providerId, instanceUrl });
  if (!identity) return normalizedPath;
  return `${identity.instanceHost}/${identity.namespacePath}`;
}
